import React, { useCallback } from 'react';
import { Padding } from '@extensions/AttributePanel/components/attributes/Padding';
import { BackgroundColor } from '@extensions/AttributePanel/components/attributes/BackgroundColor';
import { BorderWidth } from '@extensions/AttributePanel/components/attributes/BorderWidth';
import { BorderStyle } from '@extensions/AttributePanel/components/attributes/BorderStyle';
import { BorderColor } from '@extensions/AttributePanel/components/attributes/BorderColor';
import { Collapse, Grid, Switch } from '@arco-design/web-react';
import { useBlock, useFocusIdx, useEditorProps } from '@bay-email/editor';
import { BasicType, BlockManager } from '@bay-email/core';
import { ClassName } from '../../attributes/ClassName';
import { CollapseWrapper } from '../../attributes/CollapseWrapper';
import { ImageUploaderField, SelectField } from '@extensions/components/Form';
import { t } from '@bay-email/core';
import { BorderWidthControl } from '../shared/BorderWidthControl';
import { BorderRadiusControl } from '../shared/BorderRadiusControl';
import { useClearAttachedListingWhenUnused } from '../shared/useClearAttachedListingWhenUnused';

const backgroundRepeatOptions = [
	{
		value: 'no-repeat',
		get label() {
			return t('No repeat');
		},
	},
	{
		value: 'repeat',
		get label() {
			return t('Repeat');
		},
	},
	{
		value: 'repeat-x',
		get label() {
			return t('Repeat X');
		},
	},
	{
		value: 'repeat-y',
		get label() {
			return t('Repeat Y');
		},
	},
];

const backgroundSizeOptions = [
	{
		value: 'auto',
		get label() {
			return t('Auto');
		},
	},
	{
		value: 'cover',
		get label() {
			return t('Cover');
		},
	},
	{
		value: 'contain',
		get label() {
			return t('Contain');
		},
	},
];

export function Section() {
	const { focusBlock, setFocusBlock } = useBlock();
	const { focusIdx } = useFocusIdx();
	const { onUploadImage } = useEditorProps();
	const noWrap = focusBlock?.data.value.noWrap;
	const reverseColumnOrder = focusBlock?.attributes?.direction === 'rtl';
	// No Listing Settings here on purpose. A listing is owned by a Custom
	// Listing Wrapper or by an individual Listing Column — never by a whole
	// section — so a section has nothing to attach one to.
	// Any listing left on an old section is cleared below.
	useClearAttachedListingWhenUnused(focusBlock, false);

	const onChange = useCallback(
		(checked: boolean) => {
			if (!focusBlock) return;
			focusBlock.data.value.noWrap = checked;
			if (checked) {
				const children = [...focusBlock.children];
				for (let i = 0; i < children.length; i++) {
					const child = children[i];
					if (!child) continue;
					if (child.type === BasicType.GROUP) {
						children.splice(i, 1, ...child.children);
					}
				}
				focusBlock.children = [
					BlockManager.getBlockByType(BasicType.GROUP)!.create({
						children: children,
					}),
				];
			} else {
				if (
					focusBlock.children.length === 1 &&
					focusBlock.children[0].type === BasicType.GROUP
				) {
					focusBlock.children = focusBlock.children[0]?.children || [];
				}
			}
			setFocusBlock({ ...focusBlock });
		},
		[focusBlock, setFocusBlock],
	);

	const onToggleReverseColumnOrder = useCallback(
		(checked: boolean) => {
			if (!focusBlock) return;
			focusBlock.attributes.direction = checked ? 'rtl' : 'ltr';
			setFocusBlock({ ...focusBlock });
		},
		[focusBlock, setFocusBlock],
	);

	return (
		<CollapseWrapper
			isChevron
			iconPosition='right'
			defaultActiveKey={['0', '1', '2']}
			style={{ padding: 0 }}
		>
			<Collapse.Item
				name='0'
				header={t('Content Block Settings')}
				contentStyle={{ padding: 0 }}
			>
				<div className='p-3'>
					<ImageUploaderField
						labelHidden
						headerTitle={t('Background image')}
						name={`${focusIdx}.attributes.background-url`}
						uploadHandler={onUploadImage}
						context='image-src'
						isImageCropperAllowed={true}
						showLink={false}
					/>
					<BackgroundColor />
					<Grid.Row gutter={16}>
						<Grid.Col span={12}>
							<div style={{ paddingRight: '8px' }}>
								<SelectField
									label={t('Background Repeat')}
									name={`${focusIdx}.attributes.background-repeat`}
									options={backgroundRepeatOptions}
									className='custom-select-right-caret'
								/>
							</div>
						</Grid.Col>
						<Grid.Col span={12}>
							<div style={{ paddingLeft: '8px' }}>
								<SelectField
									label={t('Background size')}
									name={`${focusIdx}.attributes.background-size`}
									options={backgroundSizeOptions}
									className='custom-select-right-caret'
								/>
							</div>
						</Grid.Col>
					</Grid.Row>
					<div
						style={{
							display: 'flex',
							flexDirection: 'row',
							alignItems: 'center',
							marginTop: '16px',
						}}
					>
						<Switch
							checked={noWrap}
							checkedText={t('True')}
							uncheckedText={t('False')}
							onChange={onChange}
							size='small'
						/>
						<label style={{ paddingLeft: '12px', marginBottom: 0 }}>{t('Group')}</label>
					</div>
					<div
						style={{
							display: 'flex',
							flexDirection: 'row',
							alignItems: 'center',
							marginTop: '16px',
						}}
					>
						<Switch
							checked={reverseColumnOrder}
							checkedText={t('True')}
							uncheckedText={t('False')}
							onChange={onToggleReverseColumnOrder}
							size='small'
						/>
						<label style={{ paddingLeft: '12px', marginBottom: 0 }}>
							{t('Reverse column order')}
						</label>
					</div>
				</div>
			</Collapse.Item>
			<Collapse.Item
				name='1'
				header={t('Dimension')}
				contentStyle={{ padding: 0 }}
			>
				<div className='p-3'>
					<Padding />
				</div>
			</Collapse.Item>
			<Collapse.Item
				name='2'
				header={t('Styling')}
				contentStyle={{ padding: 0 }}
			>
				<div className='p-3'>
					<div style={{ fontWeight: 600, marginBottom: '12px', display: 'block' }}>
						{t('Border')}
					</div>
					<Grid.Row gutter={16}>
						<Grid.Col span={16}>
							<BorderColor secondary />
						</Grid.Col>
						<Grid.Col span={8}>
							<BorderStyle secondary />
						</Grid.Col>
					</Grid.Row>
					<BorderWidthControl />
					<Grid.Row gutter={16}>
						<Grid.Col span={12}>
							<BorderRadiusControl />
						</Grid.Col>
					</Grid.Row>
				</div>
			</Collapse.Item>
			<Collapse.Item
				name='4'
				header={t('Extra')}
				contentStyle={{ padding: 0 }}
			>
				<div className='p-3'>
					<ClassName />
				</div>
			</Collapse.Item>
		</CollapseWrapper>
	);
}
